import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import type { RunbookDefinitionDTO } from '@deepguide-ai/types'
import { atom, useAtom, useSetAtom } from 'jotai'
import {
  Check,
  ChevronDown,
  Copy,
  Download,
  History,
  Loader2,
  Lock,
  MoreHorizontal,
  Pencil,
  Play,
  Sparkles,
  Trash2,
  Unlock,
  UploadCloud,
  X,
  AlertTriangle,
} from 'lucide-react'
import { toast } from 'sonner'
import { screenataPageContextAtom } from '@/stores/screenataPageContextStore'
import { trpc } from '@/lib/trpc'
import { cn } from '@/lib/utils'
import { useRunbookRun } from '@/hooks/useRunbookRun'
import { RunbookRunsTable } from '@/components/compliance/RunbookRunsTable'
import { useWorkspacePermissions } from '@/hooks/useWorkspacePermissions'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@deepguide-ai/ui/alert-dialog'
import { Badge } from '@deepguide-ai/ui/badge'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@deepguide-ai/ui/breadcrumb'
import { Button } from '@deepguide-ai/ui/button'
import { Checkbox } from '@deepguide-ai/ui/checkbox'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@deepguide-ai/ui/dropdown-menu'
import { Input } from '@deepguide-ai/ui/input'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@deepguide-ai/ui/dialog'
import { ScrollArea } from '@deepguide-ai/ui/scroll-area'
import { Skeleton } from '@deepguide-ai/ui/skeleton'
import { Textarea } from '@deepguide-ai/ui/textarea'
import { AskAiButton } from '@/components/chat/AskAiButton'
import { useAskAi } from '@/components/chat/useAskAi'
import {
  ControlSelector,
  type ControlWithFramework,
} from '@/components/compliance/ControlSelector'
import { RunbookDefinitionGraph } from '@/components/compliance/RunbookDefinitionGraph'
import {
  analyzeRunbookInputUsage,
  buildInitialRunbookInputValues,
  findBrowserProfileInputKey,
  hasRunbookInputs,
  RunbookInputsForm,
  selectRunbookExecutionParams,
  withBrowserIdentityInput,
  type RunbookInputValues,
} from '@/components/compliance/RunbookInputsForm'
import {
  buildRunbookAnalysisParamsFromDrafts,
  buildRunbookParamDrafts,
  buildRunbookParamMapFromDrafts,
  RunbookParamEditor,
  type RunbookParamDraft,
} from '@/components/compliance/RunbookParamEditor'
import { RunbookParamsSection } from '@/components/compliance/RunbookParamsSection'
import { RunbookRunDetail } from '@/components/compliance/RunbookRunDetail'
import { ScreenataPageLayout } from '@/components/compliance/ScreenataPageLayout'
import {
  buildRunbookDraftFromDefinition,
  buildRunbookDocumentBody,
} from '@/components/compliance/runbook-draft'
import { toRunbookTransferSteps } from '@/components/compliance/runbook-transfer'
import {
  buildRunbookValidationFixPrompt,
  getRunbookPreflightIssueLocation,
  groupRunbookPreflightIssues,
} from '@/components/compliance/runbook-preflight'

export const Route = createFileRoute(
  '/(protected)/workspaces/$workspaceId/runbooks_/$runbookId'
)({
  // #1100 — an SOP-ingest draft arrives with its suggested control ids so the
  // editor can surface a "review & activate" banner.
  validateSearch: (
    search: Record<string, unknown>
  ): { ingestControls?: string; ingestSlots?: string } => ({
    ingestControls:
      typeof search.ingestControls === 'string'
        ? search.ingestControls
        : undefined,
    // Canonical `slotKey::scopeKey` tuples the ingest mapped to. Controls are
    // derived from these; the tuples are what a later "bind to an existing
    // Test" step matches on, so they ride along rather than being re-inferred.
    ingestSlots:
      typeof search.ingestSlots === 'string' ? search.ingestSlots : undefined,
  }),
  component: RunbookDetailPage,
})

// Autosave fires this long after the last edit (mirrors the policy editor's
// debounced save so users never lose work by forgetting to hit Save).
const AUTOSAVE_DEBOUNCE_MS = 1500

// Opening the global Vera rail moves the workspace <Outlet /> into a different
// layout branch, which remounts this route. Keep inspector selection above that
// remount boundary in the application store, scoped by runbook id. This is
// session-only UI state — it is neither persisted nor part of the definition.
const selectedRunbookStepByIdAtom = atom<Record<string, string | null>>({})

// A stable string of the user-editable content. Both the dirty check and the
// "did anything change while a save was in flight" guard compare these — the
// SAME shape on both sides, so there's no rebuilt-vs-raw asymmetry that would
// falsely read as dirty (which previously made autosave loop and flash
// "Saving"). Params compare as drafts (not the rebuilt param map) for the same
// reason. Steps/dependencies reflect the live graph edits (graphDraft) so the
// signature always mirrors the editor's latest content.
function makeContentSignature(input: {
  title: string
  description: string
  steps: unknown
  dependencies: unknown
  outputs: unknown
  paramDrafts: unknown
}): string {
  return JSON.stringify({
    title: input.title.trim(),
    description: input.description.trim(),
    steps: input.steps,
    dependencies: input.dependencies,
    outputs: input.outputs,
    params: input.paramDrafts,
  })
}

function isActiveRunStatus(status?: string | null): boolean {
  return status === 'running' || status === 'awaiting_human'
}

interface RunsSectionRun {
  id: string
  status: string
  executedAt: Date | string | null
  executionTime?: number | null
  totalCost?: number | null
  totalSteps?: number | null
  successfulSteps?: number | null
  scheduledExecutionId?: string | null
  errorMessage?: string | null
  startedByUser?: {
    name?: string | null
    email?: string | null
    image?: string | null
    isServiceAccount?: boolean
  } | null
}

function RunbookRunsSection({
  runs,
  activeRunId,
  isLoading,
  onSelect,
  hideHeading,
}: {
  runs: RunsSectionRun[]
  activeRunId: string | null
  isLoading: boolean
  onSelect: (runId: string) => void
  hideHeading?: boolean
}) {
  return (
    <section>
      {!hideHeading && (
        <div className='text-text-body mb-2 flex items-center gap-2 text-[12px] font-medium tracking-wide uppercase'>
          <Play className='h-3.5 w-3.5' />
          Runs
        </div>
      )}

      <RunbookRunsTable
        runs={runs}
        isLoading={isLoading}
        onRowClick={onSelect}
        activeRunId={activeRunId}
        emptyState='No runs yet. Click Run to execute this procedure.'
      />
    </section>
  )
}

/** Minimal view of a run's result the Output pane reads. Kept structural so the
 *  pane doesn't couple to the full `runbook.getRun` shape. */
interface RunOutputView {
  status?: string | null
  errorMessage?: string | null
  outputs?: unknown
  results?: unknown
}

type OutputField = { key: string; type: string; value: unknown; isError?: boolean }

function isPlainRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

/** A non-empty plain object, or null. */
function nonEmptyRecord(value: unknown): Record<string, unknown> | null {
  return isPlainRecord(value) && Object.keys(value).length > 0 ? value : null
}

function outputValueType(value: unknown): string {
  if (value === null || value === undefined) return 'null'
  if (Array.isArray(value) || typeof value === 'object') return 'json'
  return typeof value // 'string' | 'number' | 'boolean'
}

/** Project a run's result into the typed field list the Output pane renders.
 *  Real `outputs` (falling back to `results`) win when present — a run can
 *  carry a stale `errorMessage` alongside a successful result, and hiding the
 *  output behind the error would be misleading. Only when there is no usable
 *  output does an `errorMessage` surface as a single `error: string` field
 *  (sim.ai parity). */
function buildOutputFields(run: RunOutputView): {
  fields: OutputField[]
  raw: unknown
} {
  const source = nonEmptyRecord(run.outputs) ?? nonEmptyRecord(run.results)
  if (source) {
    return {
      fields: Object.entries(source).map(([key, value]) => ({
        key,
        type: outputValueType(value),
        value,
      })),
      raw: source,
    }
  }
  if (run.errorMessage) {
    return {
      fields: [
        { key: 'error', type: 'string', value: run.errorMessage, isError: true },
      ],
      raw: { error: run.errorMessage },
    }
  }
  return { fields: [], raw: null }
}

/** One collapsible `key  type ⌄` row over its formatted value. */
function OutputFieldRow({ field }: { field: OutputField }) {
  const [open, setOpen] = useState(true)
  const display =
    typeof field.value === 'string'
      ? `"${field.value}"`
      : JSON.stringify(field.value, null, 2)
  return (
    <div>
      <button
        type='button'
        onClick={() => setOpen((value) => !value)}
        className='flex w-full items-center gap-2 text-left'
      >
        <span
          className={`text-[12px] font-medium ${
            field.isError ? 'text-status-red' : 'text-cobalt-strong'
          }`}
        >
          {field.key}
        </span>
        <span
          className={`rounded-sm px-1.5 py-0.5 text-[10px] ${
            field.isError
              ? 'bg-[#FEE4E2] text-status-red'
              : 'bg-muted text-muted-foreground'
          }`}
        >
          {field.type}
        </span>
        <ChevronDown
          className={`ml-auto h-3.5 w-3.5 text-text-placeholder transition-transform ${
            open ? '' : '-rotate-90'
          }`}
        />
      </button>
      {open && (
        <pre className='text-text-body mt-1 ml-2 overflow-x-auto pl-3 text-[12px] whitespace-pre-wrap'>
          {display}
        </pre>
      )}
    </div>
  )
}

/** sim.ai-style Output pane — the result (or error) of the selected run, shown
 *  beside Logs once a run has executed. */
function OutputPanel({ run }: { run: RunOutputView }) {
  const { fields, raw } = useMemo(() => buildOutputFields(run), [run])
  return (
    <div className='flex h-full flex-col'>
      <div className='flex h-9 shrink-0 items-center gap-2 px-4'>
        <span className='text-[12px] font-medium text-text-secondary'>Output</span>
        {raw != null && (
          <button
            type='button'
            onClick={() => {
              void navigator.clipboard.writeText(JSON.stringify(raw, null, 2))
              toast.success('Output copied')
            }}
            className='ml-auto text-text-placeholder transition-colors hover:text-text-secondary'
            aria-label='Copy output'
          >
            <Copy className='h-3.5 w-3.5' />
          </button>
        )}
      </div>
      <ScrollArea type='scroll' className='min-h-0 flex-1' viewClassName='[&>div]:!block'>
        <div className='space-y-2 px-4 py-3'>
          {fields.length === 0 ? (
            <p className='text-[12px] text-text-placeholder'>No output.</p>
          ) : (
            fields.map((field) => (
              <OutputFieldRow key={field.key} field={field} />
            ))
          )}
        </div>
      </ScrollArea>
    </div>
  )
}

/** RFC-187 — run logs and selected-run output. Kept together so the menu
 * entry and automatic post-run view use the same data and interactions. */
function LogsDialog({
  open,
  onOpenChange,
  runs,
  isLoading,
  activeRunId,
  onSelect,
  output,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  runs: RunsSectionRun[]
  isLoading: boolean
  activeRunId: string | null
  onSelect: (runId: string) => void
  /** Output pane for the selected run; only rendered once a run has executed. */
  output?: ReactNode
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='grid h-[min(560px,calc(100dvh-2rem))] !max-w-[70%] grid-rows-[auto_minmax(0,1fr)] overflow-hidden'>
        <DialogHeader>
          <DialogTitle className='font-heading'>Logs</DialogTitle>
          <DialogDescription>
            Review procedure runs and their output.
          </DialogDescription>
        </DialogHeader>
        <div className='flex min-h-0 gap-4 px-6 pb-6'>
          <div
            className={cn(
              'min-w-0',
              output ? 'border-border w-1/2 border-r pr-4' : 'w-full'
            )}
          >
            <ScrollArea
              type='scroll'
              className='h-full'
              viewClassName='[&>div]:!block'
            >
              <RunbookRunsSection
                runs={runs}
                activeRunId={activeRunId}
                isLoading={isLoading}
                onSelect={onSelect}
                hideHeading
              />
            </ScrollArea>
          </div>
          {output && <div className='w-1/2 min-w-0'>{output}</div>}
        </div>
      </DialogContent>
    </Dialog>
  )
}

function RunbookDetailPage() {
  const { workspaceId, runbookId } = Route.useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const openAskAi = useAskAi()
  const { canWorkspaceWrite } = useWorkspacePermissions(workspaceId)
  const [selectedRunbookStepById, setSelectedRunbookStepById] = useAtom(
    selectedRunbookStepByIdAtom
  )
  const selectedGraphStepId = selectedRunbookStepById[runbookId] ?? null
  const setSelectedGraphStepId = useCallback(
    (stepId: string | null) => {
      setSelectedRunbookStepById((current) =>
        current[runbookId] === stepId
          ? current
          : { ...current, [runbookId]: stepId }
      )
    },
    [runbookId, setSelectedRunbookStepById]
  )

  const [selectedRunId, setSelectedRunId] = useState<string | null>(null)
  // RFC-187 — Logs dialog: closed by default, auto-opens after a run starts.
  const [logsOpen, setLogsOpen] = useState(false)
  // #1100 — an SOP-ingest draft lands here with suggested control ids; surface
  // a "review & activate" banner. Captured once, then the search param is
  // cleared so a refresh doesn't re-trigger it.
  const search = Route.useSearch()
  const [importedControlIds, setImportedControlIds] = useState<string[]>([])
  const [importBannerDismissed, setImportBannerDismissed] = useState(false)
  // Declared early because `controlsQuery` is gated on it (controls load only
  // when the create-test dialog opens, e.g. from the SOP-import banner).
  const [createTestDialogOpen, setCreateTestDialogOpen] = useState(false)
  useEffect(() => {
    const raw = search.ingestControls
    if (!raw) return
    const ids = raw.split(',').filter(Boolean)
    if (ids.length > 0) setImportedControlIds(ids)
    navigate({
      to: '/workspaces/$workspaceId/runbooks/$runbookId',
      params: { workspaceId, runbookId },
      search: {},
      replace: true,
    })
    // Capture once on arrival; subsequent renders shouldn't re-read the param.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  const [runInputDialogOpen, setRunInputDialogOpen] = useState(false)
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  // RFC-206 §5.1 — routing statement; saved via runbook.setConditions (which
  // also reconciles the managed task.triage trigger), not saveDefinition.
  const [conditions, setConditions] = useState('')
  // RFC-206 §5.5 — per-procedure opt-in for Vera auto-start on a
  // high-confidence free-text match. Default off.
  const [autoTriage, setAutoTriage] = useState(false)
  const [documentBody, setDocumentBody] = useState<unknown | null>(null)
  const [steps, setSteps] = useState<RunbookDefinitionDTO['steps']>([])
  const [dependencies, setDependencies] = useState<
    RunbookDefinitionDTO['dependencies']
  >([])
  const [hydratedDefinitionId, setHydratedDefinitionId] = useState<
    string | null
  >(null)
  const [runbookParamDrafts, setRunbookParamDrafts] = useState<
    RunbookParamDraft[]
  >([])
  // Content signature of the last persisted (or freshly hydrated) state. Dirty =
  // current signature differs from this. Set on hydration and after each save.
  const [savedSignature, setSavedSignature] = useState<string | null>(null)
  // The editor is always editable (no view/edit toggle); locked or viewer
  // procedures are read-only. `skipRehydrateRef` guards against a background
  // refetch (e.g. window focus) clobbering unsaved edits — it's reset on save
  // so the post-save refetch re-hydrates the normalized server state.
  const skipRehydrateRef = useRef(false)
  // RFC-167 §13.6 — document (TipTap) vs. graph (React Flow) rendering of the
  // steps. Branched procedures default to graph; seeded from the saved
  // viewPreference on hydration, then toggled freely for the session.
  // RFC-167 — Document view retired (graph is the only definition view). Kept
  // as state (always 'graph') so the save path's viewPreference + the
  // full-width layout branch stay wired.
  const [definitionView, setDefinitionView] = useState<'document' | 'graph'>(
    'graph'
  )
  // RFC-167 §13.6 — unsaved FULL structural edit from the graph editor: the
  // complete {steps, dependencies} graph (steps carry kind/role/actions +
  // durable ids). null = the graph wasn't edited this session (save uses the
  // document path). When set, save persists it through the structured steps
  // path. Reset on load / save / cancel.
  const [graphDraft, setGraphDraft] = useState<{
    steps: RunbookDefinitionDTO['steps']
    dependencies: RunbookDefinitionDTO['dependencies']
    outputs?: RunbookDefinitionDTO['outputs']
  } | null>(null)
  const definitionQuery = useQuery({
    ...trpc.runbook.getDefinition.queryOptions({ id: runbookId }),
    enabled: !!runbookId,
    throwOnError: false,
  })

  const definition = definitionQuery.data as RunbookDefinitionDTO | undefined
  const displayedSteps = graphDraft?.steps ?? definition?.steps ?? []
  // Show the browser-identity picker at run time only when the procedure drives
  // a browser (RFC-167). Non-browser runbooks keep the plain run flow.
  const hasBrowserStep = useMemo(
    () =>
      (definition?.steps ?? []).some(
        (step) =>
          step.runner === 'browser' || step.automated?.runner === 'browser',
      ),
    [definition?.steps],
  )
  const definitionDraft = useMemo(
    () => (definition ? buildRunbookDraftFromDefinition(definition) : null),
    [definition]
  )

  const setPageContext = useSetAtom(screenataPageContextAtom)
  useEffect(() => {
    if (!definition) return
    setPageContext({
      page: 'runbook-detail',
      summary: `Runbook: ${definition.title || 'Untitled runbook'}`,
      data: {
        runbookId: definition.id,
        runbookTitle: definition.title,
        runbookDescription: definition.description ?? undefined,
      },
    })
    return () => setPageContext(null)
  }, [definition, setPageContext])

  useEffect(() => {
    setHydratedDefinitionId(null)
    skipRehydrateRef.current = false
  }, [runbookId])

  // Hydrate (or re-hydrate after a save) local edit state from the
  // server definition. Once hydrated, only re-hydrate for a brand-new
  // procedure or right after our own save (skipRehydrateRef is reset there);
  // a background refetch must not clobber the user's in-progress edits.
  useEffect(() => {
    if (!definition || !definitionDraft) return
    const isNewDefinition = hydratedDefinitionId !== definition.id
    if (!isNewDefinition && skipRehydrateRef.current) return

    const hydratedParamDrafts = buildRunbookParamDrafts({
      params: definition.params,
    })
    setTitle(definition.title || '')
    setDescription(definition.description || '')
    setConditions(definition.conditions || '')
    setAutoTriage(definition.autoTriageFreeText === true)
    setDocumentBody(definitionDraft.documentBody)
    setSteps(definitionDraft.steps)
    setDependencies(definitionDraft.dependencies)
    setRunbookParamDrafts(hydratedParamDrafts)
    // Baseline the dirty check against exactly what we just hydrated, so a
    // freshly loaded (or just-saved) procedure reads as clean.
    setSavedSignature(
      makeContentSignature({
        title: definition.title || '',
        description: definition.description || '',
        // Baseline against the SAME source the graph renders and the save
        // persists (`definition.*`), not `definitionDraft.*` (re-extracted from
        // the documentBody) — otherwise the two halves disagree and either
        // false-dirty or, worse, an output edit saves the draft's empty deps.
        steps: definition.steps,
        dependencies: definition.dependencies,
        // Must match currentSignature's filter exactly (complete outputs only),
        // or a freshly-loaded procedure reads as permanently dirty.
        outputs: (definition.outputs ?? []).filter(
          (output) => output.name.trim() && output.ref.trim(),
        ),
        paramDrafts: hydratedParamDrafts,
      })
    )
    if (isNewDefinition) {
      setDefinitionView('graph')
    }
    setGraphDraft(null)
    setHydratedDefinitionId(definition.id)
    skipRehydrateRef.current = true
    // `hydratedDefinitionId` is read inside the guard but should not itself
    // trigger hydration; only a new definition (post-save refetch, runbook
    // switch) should.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [definition, definitionDraft])

  const isDefinitionHydrated =
    !!definition && hydratedDefinitionId === definition.id

  const runbookParamBuild = useMemo(
    () => buildRunbookParamMapFromDrafts(runbookParamDrafts),
    [runbookParamDrafts]
  )

  const runbookAnalysisParams = useMemo(
    () => buildRunbookAnalysisParamsFromDrafts(runbookParamDrafts),
    [runbookParamDrafts]
  )

  const runbookInputUsage = useMemo(
    () => analyzeRunbookInputUsage(steps, runbookAnalysisParams),
    [runbookAnalysisParams, steps]
  )

  const executionRunbookInputParams = useMemo(() => {
    if (runbookParamBuild.error) return {}

    return selectRunbookExecutionParams(
      runbookParamBuild.params,
      runbookInputUsage.usedInputNames
    )
  }, [runbookParamBuild, runbookInputUsage.usedInputNames])

  // The browser-identity input is rendered inside the inputs form (RFC-167 —
  // unified run dialog). Recording-compiled procedures declare one; synthesize a
  // transient one for older browser runbooks so the picker still appears.
  const effectiveRunParams = useMemo(
    () => withBrowserIdentityInput(executionRunbookInputParams, hasBrowserStep),
    [executionRunbookInputParams, hasBrowserStep]
  )

  const [runbookInputValues, setRunbookInputValues] =
    useState<RunbookInputValues>(() =>
      buildInitialRunbookInputValues(effectiveRunParams)
    )

  useEffect(() => {
    setRunbookInputValues(buildInitialRunbookInputValues(effectiveRunParams))
  }, [effectiveRunParams])

  const runsQuery = useQuery({
    ...trpc.runbook.listRuns.queryOptions({ workflowId: runbookId, limit: 20 }),
    enabled: !!runbookId,
    throwOnError: false,
  })
  const validationQuery = useQuery({
    ...trpc.runbook.validateDefinition.queryOptions({
      id: runbookId,
      mode: 'preflight',
    }),
    enabled: !!runbookId,
    throwOnError: false,
    refetchOnMount: 'always',
  })
  const [validationDialogOpen, setValidationDialogOpen] = useState(false)
  const [validationRunning, setValidationRunning] = useState(false)
  const showValidationFailureOrToast = async (fallbackMessage: string) => {
    const refreshed = await validationQuery.refetch()
    if (refreshed.data && !refreshed.data.ok) {
      setValidationDialogOpen(true)
      return
    }
    toast.error(fallbackMessage)
  }
  const controlsQuery = useQuery({
    ...trpc.guidedTemplate.listAllControls.queryOptions({
      enabledFrameworks: true,
    }),
    enabled: createTestDialogOpen,
  })
  const selectedRunDetailsQuery = useRunbookRun({
    executionId: selectedRunId,
    fallbackPollMs: 2500,
    isActive: (status) => isActiveRunStatus(status),
  })

  const invalidateDefinition = () =>
    queryClient.invalidateQueries({
      queryKey: trpc.runbook.getDefinition.queryKey({ id: runbookId }),
    })

  const invalidateRuns = () =>
    queryClient.invalidateQueries({
      queryKey: trpc.runbook.listRuns.queryKey({ workflowId: runbookId }),
    })
  const invalidateValidation = () =>
    queryClient.invalidateQueries({
      queryKey: trpc.runbook.validateDefinition.queryKey({
        id: runbookId,
        mode: 'preflight',
      }),
    })
  // RFC-200: draft/published state (published version + unpublished-changes flag).
  const invalidatePublishState = () =>
    queryClient.invalidateQueries({
      queryKey: trpc.runbook.getPublishState.queryKey({ id: runbookId }),
    })
  const publishStateQuery = useQuery({
    ...trpc.runbook.getPublishState.queryOptions({ id: runbookId }),
    enabled: !!runbookId,
    throwOnError: false,
  })

  const selectedRunStatus = selectedRunDetailsQuery.data?.status
  useEffect(() => {
    if (!selectedRunId) return
    if (!selectedRunStatus) return
    if (isActiveRunStatus(selectedRunStatus)) return
    invalidateRuns()
    // invalidateRuns is a stable closure over runbookId/queryClient
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRunId, selectedRunStatus, runbookId])

  const executeMutation = useMutation({
    ...trpc.runbook.execute.mutationOptions(),
    onSuccess: (result) => {
      setSelectedRunId(result.executionId)
      setLogsOpen(true)
      invalidateRuns()
    },
    onError: async (error) => {
      await showValidationFailureOrToast(
        `Failed to run procedure: ${error.message}`
      )
      invalidateRuns()
    },
  })

  // Autosave surfaces progress through this status pill instead of a toast
  // (the policy editor pattern) — a toast per debounced save would be spammy.
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved'>(
    'idle'
  )
  const savedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const failedAutosaveSignatureRef = useRef<string | null>(null)

  const saveMutation = useMutation({
    ...trpc.runbook.saveDefinition.mutationOptions(),
    onMutate: () => {
      setSaveStatus('saving')
      // Snapshot what we're persisting so onSuccess can tell whether the user
      // kept editing while the request was in flight.
      savingSignatureRef.current = currentSignatureRef.current
    },
    onSuccess: () => {
      failedAutosaveSignatureRef.current = null
      setEditDetailsOpen(false)
      invalidateDefinition()
      invalidateValidation()
      // Draft changed ⇒ refresh "unpublished changes" state.
      invalidatePublishState()
      if (currentSignatureRef.current === savingSignatureRef.current) {
        // No edits during the save — let the refetch re-hydrate from the
        // normalized server definition. The rehydrate effect clears graphDraft
        // atomically with the fresh steps, so the canvas never flickers back to
        // pre-edit content in the gap before the refetch lands.
        skipRehydrateRef.current = false
      } else {
        // The user kept editing while this save was in flight. Keep their newer
        // edits (suppress the refetch re-hydrate so it can't clobber them) and
        // baseline against what we just sent, so the still-newer edits remain
        // dirty and autosave persists them next.
        skipRehydrateRef.current = true
        setSavedSignature(savingSignatureRef.current)
      }
      setSaveStatus('saved')
      if (savedTimerRef.current) clearTimeout(savedTimerRef.current)
      savedTimerRef.current = setTimeout(() => setSaveStatus('idle'), 2000)
    },
    onError: (error) => {
      failedAutosaveSignatureRef.current = savingSignatureRef.current
      toast.error(`Failed to save procedure: ${error.message}`)
      setSaveStatus('idle')
    },
  })

  // RFC-200: publish the current draft (strict validation → snapshot → active).
  const publishMutation = useMutation({
    ...trpc.runbook.publishDefinition.mutationOptions(),
    onSuccess: (state: { publishedVersion: number | null }) => {
      toast.success(
        state.publishedVersion
          ? `Published v${state.publishedVersion}`
          : 'Published'
      )
      invalidatePublishState()
      // The Procedures list badges Draft vs Published off listRunbooks'
      // publishedVersion; refresh it so the status flips without a reload.
      queryClient.invalidateQueries({
        queryKey: trpc.runbook.listRunbooks.queryKey(),
      })
    },
    onError: async (error) => {
      await showValidationFailureOrToast(`Can't publish: ${error.message}`)
    },
  })

  // RFC-200: restore a published version INTO the draft (non-destructive).
  const [versionHistoryOpen, setVersionHistoryOpen] = useState(false)
  const versionsQuery = useQuery({
    ...trpc.runbook.getVersions.queryOptions({ id: runbookId }),
    enabled: !!runbookId && versionHistoryOpen,
    throwOnError: false,
  })
  const restoreVersionMutation = useMutation({
    ...trpc.runbook.restoreVersion.mutationOptions(),
    onSuccess: (_result, variables: { version: number }) => {
      // Pull the restored draft back into the editor and refresh publish state.
      failedAutosaveSignatureRef.current = null
      skipRehydrateRef.current = false
      invalidateDefinition()
      invalidateValidation()
      invalidatePublishState()
      setVersionHistoryOpen(false)
      toast.success(
        `Restored v${variables.version} to draft. Review and publish to make it live`
      )
    },
    onError: (error) => {
      toast.error(`Failed to restore: ${error.message}`)
    },
  })

  const [editDetailsOpen, setEditDetailsOpen] = useState(false)
  const [selectedCreateTestControlIds, setSelectedCreateTestControlIds] =
    useState<string[]>([])
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const deleteMutation = useMutation({
    ...trpc.runbook.deleteRunbook.mutationOptions(),
    onSuccess: () => {
      toast.success('Procedure deleted')
      queryClient.invalidateQueries({
        queryKey: trpc.runbook.listRunbooks.queryKey(),
      })
      navigate({
        to: '/workspaces/$workspaceId/runbooks',
        params: { workspaceId },
        replace: true,
      })
    },
    onError: (error: unknown) => {
      const message =
        error instanceof Error ? error.message : 'Failed to delete procedure'
      toast.error(message)
    },
  })
  const duplicateMutation = useMutation({
    ...trpc.runbook.duplicateRunbook.mutationOptions(),
    onSuccess: (result: { id: string }) => {
      toast.success('Procedure duplicated')
      queryClient.invalidateQueries({
        queryKey: trpc.runbook.listRunbooks.queryKey(),
      })
      navigate({
        to: '/workspaces/$workspaceId/runbooks/$runbookId',
        params: { workspaceId, runbookId: result.id },
      })
    },
    onError: (error: unknown) => {
      const message =
        error instanceof Error ? error.message : 'Failed to duplicate procedure'
      toast.error(message)
    },
  })

  const lockMutation = useMutation({
    ...trpc.runbook.setLock.mutationOptions(),
    onSuccess: (result: { locked: boolean }) => {
      toast.success(result.locked ? 'Procedure locked' : 'Procedure unlocked')
      invalidateDefinition()
    },
    onError: (error: unknown) => {
      const message =
        error instanceof Error ? error.message : 'Failed to update lock'
      toast.error(message)
    },
  })

  // Client-side export: serialize the loaded definition to a JSON file the user
  // can re-import or archive. No server round-trip needed.
  const handleExport = useCallback(async () => {
    if (!definition) return
    // Persist pending edits, then export the current (now-saved) state — the
    // refetch lands asynchronously, so read the effective edit state directly.
    await flushPendingSaveRef.current(true)
    const assignmentOptions = await queryClient.ensureQueryData(
      trpc.runbook.listAssignmentOptions.queryOptions(),
    )
    const payload = {
      title: title.trim() || definition.title,
      description: description.trim() || definition.description,
      runbookKind: definition.runbookKind,
      runbookCategory: definition.runbookCategory,
      params: runbookParamBuild.error
        ? definition.params
        : runbookParamBuild.params,
      steps: toRunbookTransferSteps(
        graphDraft?.steps ?? definition.steps,
        assignmentOptions,
      ),
      dependencies: graphDraft?.dependencies ?? definition.dependencies,
      exportedAt: new Date().toISOString(),
    }
    const slug =
      (title.trim() || definition.title || 'runbook')
        .toLowerCase()
        .replace(/[\s/]+/g, '-')
        .replace(/[^a-z0-9-]/g, '') || 'runbook'
    const blob = new Blob([JSON.stringify(payload, null, 2)], {
      type: 'application/json',
    })
    const url = URL.createObjectURL(blob)
    const anchor = document.createElement('a')
    anchor.href = url
    anchor.download = `${slug}.json`
    anchor.click()
    URL.revokeObjectURL(url)
    toast.success('Procedure exported')
  }, [
    definition,
    title,
    description,
    graphDraft,
    runbookParamBuild,
    queryClient,
  ])
  const createLinkedTestMutation = useMutation({
    ...trpc.runbook.createTests.mutationOptions(),
    onSuccess: (result) => {
      const firstTest = result.tests[0]
      toast.success(
        result.tests.length === 1
          ? 'Procedure test is ready'
          : `${result.tests.length} procedure tests are ready`
      )
      setCreateTestDialogOpen(false)
      queryClient.invalidateQueries({
        queryKey: trpc.runbook.listRunbooks.queryKey(),
      })
      invalidateDefinition()
      if (firstTest?.id) {
        navigate({
          to: '/workspaces/$workspaceId/tests/$testId',
          params: { workspaceId, testId: firstTest.id },
        })
      }
    },
    onError: (error) => {
      toast.error(`Failed to create procedure test: ${error.message}`)
    },
  })

  const allControls = (controlsQuery.data ?? []) as ControlWithFramework[]

  const createLinkedTest = () => {
    if (selectedCreateTestControlIds.length === 0) {
      toast.error('Select at least one control')
      return
    }

    createLinkedTestMutation.mutate({
      workflowId: runbookId,
      controlDefIds: selectedCreateTestControlIds,
    })
  }

  const linkedTestCount = definition?.usedByTests?.length ?? 0
  const canDelete = linkedTestCount === 0
  const locked = definition?.locked ?? false
  const deleteBlockedReason =
    linkedTestCount > 0
      ? linkedTestCount === 1
        ? 'Linked to 1 test. Unlink it first to delete this procedure.'
        : `Linked to ${linkedTestCount} tests. Unlink them first to delete this procedure.`
      : undefined

  const runCurrentValidation = async (showResult = false) => {
    if (!isDefinitionHydrated) {
      toast.error('Procedure is still loading. Wait before validating it.')
      return null
    }
    if (!title.trim()) {
      toast.error('Procedure title is required')
      return null
    }
    if (runbookParamBuild.error) {
      toast.error(runbookParamBuild.error)
      return null
    }

    setValidationRunning(true)
    try {
      const saved = await flushPendingSaveRef.current(true)
      if (!saved) return null
      const refreshed = await validationQuery.refetch()
      if (refreshed.error || !refreshed.data) {
        toast.error('Failed to validate procedure. Try again.')
        return null
      }
      if (showResult || !refreshed.data.ok) {
        setValidationDialogOpen(true)
      }
      return refreshed.data
    } finally {
      setValidationRunning(false)
    }
  }

  const handleFixValidationIssues = async (targetStepId: string) => {
    if (!definition) return

    // Keep the inspector anchored while validation refetch or opening Vera
    // remounts the graph subtree.
    setSelectedGraphStepId(targetStepId)
    const validation = await runCurrentValidation(false)
    if (!validation) return

    const currentSteps = displayedSteps
    const issues =
      groupRunbookPreflightIssues(validation.errors, currentSteps).get(
        targetStepId,
      ) ?? []

    if (issues.length === 0) {
      toast.success('No current validation issues need Vera’s help.')
      setValidationDialogOpen(false)
      return
    }

    const pagePath = `/workspaces/${workspaceId}/runbooks/${runbookId}`
    setValidationDialogOpen(false)
    await openAskAi({
      text: buildRunbookValidationFixPrompt({
        title: titleText,
        issues,
        steps: currentSteps,
      }),
      page: 'runbook-detail',
      pageSummary: `Procedure: ${titleText}`,
      pagePath,
      pageData: {
        runbookId,
        runbookTitle: titleText,
        validationScope: 'step',
        validationIssues: issues,
      },
      activityContext: {
        action: 'fixRunbookValidation',
        targetStepId,
        validationIssues: issues,
      },
      metadata: {
        intent: 'runbook-validation-fix',
        runbookId,
        targetStepId,
      },
      threadName: `Fix ${titleText}`,
      threadScope: {
        workspaceId,
        scopeType: 'runbook-detail',
        scopeId: runbookId,
        scopeLabel: titleText,
        scopePath: pagePath,
      },
      sendMessage: true,
    })
  }

  const executeRunbook = () => {
    // The browser identity is a typed input; peel its value off into the
    // dedicated `browserProfileId` (it's the run identity, not a step param) and
    // drop it from the inputs payload sent to the executor.
    const identityKey = findBrowserProfileInputKey(effectiveRunParams)
    const identityValue = identityKey ? runbookInputValues[identityKey] : null
    const browserProfileId =
      typeof identityValue === 'string' && identityValue ? identityValue : undefined

    const stepInputs: RunbookInputValues = { ...runbookInputValues }
    if (identityKey) delete stepInputs[identityKey]

    executeMutation.mutate({
      workflowId: runbookId,
      inputs: Object.keys(stepInputs).length > 0 ? stepInputs : undefined,
      browserProfileId,
    })
  }

  const handleRun = async () => {
    const validation = await runCurrentValidation()
    if (!validation?.ok) return

    // Open the run dialog when there are inputs to fill (the browser identity is
    // one of them for browser procedures); otherwise run straight away.
    if (hasRunbookInputs(effectiveRunParams)) {
      setRunInputDialogOpen(true)
      return
    }

    executeRunbook()
  }

  // The graph is the only editor now, so ALWAYS persist via the structured
  // path: send the complete {steps, dependencies} verbatim. When the user
  // edited the graph this session that's `graphDraft`; otherwise it's the
  // persisted definition — so a title/description/param-only save preserves
  // the existing step + branch structure instead of recompiling from markdown
  // (which would flatten branches and dangle their dependencies). The markdown
  // document save path is retired with the document editor.
  const buildSavePayload = (def: RunbookDefinitionDTO) => {
    const effectiveSteps = graphDraft?.steps ?? def.steps
    const effectiveDependencies = graphDraft?.dependencies ?? def.dependencies
    // Drop in-progress rows (no name or ref) — they're local UI state, never
    // persisted, so they don't round-trip and vanish on the post-save rehydrate.
    const effectiveOutputs = (graphDraft?.outputs ?? def.outputs).filter(
      (output) => output.name.trim() && output.ref.trim(),
    )
    const builtBody = buildRunbookDocumentBody({
      steps: effectiveSteps,
      dependencies: effectiveDependencies,
      baseDocumentBody: (documentBody as never) ?? null,
    })
    return {
      id: runbookId,
      title: title.trim(),
      description: description.trim() || null,
      documentBody: builtBody,
      instructionsMarkdown: (builtBody as { markdown?: string }).markdown ?? '',
      runbookParams: runbookParamBuild.params,
      steps: effectiveSteps,
      dependencies: effectiveDependencies,
      outputs: effectiveOutputs,
      viewPreference: definitionView,
      changeNote: 'Updated runbook definition',
    }
  }

  const setConditionsMutation = useMutation({
    ...trpc.runbook.setConditions.mutationOptions(),
    onSuccess: (data: { triage?: { state?: string } } | undefined) => {
      invalidateDefinition()
      if (data?.triage?.state === 'enabled') {
        toast.success('Procedure is now selectable for task triage')
      }
    },
    onError: (err: { message?: string }) => {
      toast.error(err.message ?? 'Failed to save conditions')
    },
  })

  const handleSave = () => {
    if (!definition) return

    if (!isDefinitionHydrated) {
      toast.error(
        'Procedure is still loading. Wait for the editor to finish hydrating before saving.'
      )
      return
    }

    if (!title.trim()) {
      toast.error('Procedure title is required')
      return
    }

    if (runbookParamBuild.error) {
      toast.error(runbookParamBuild.error)
      return
    }

    // RFC-206 §5.1/§5.5 — conditions + auto-triage save through their own
    // mutation so the server reconciles the managed task.triage trigger in
    // the same call.
    const nextConditions = conditions.trim() || null
    if (
      nextConditions !== ((definition.conditions ?? '').trim() || null) ||
      autoTriage !== (definition.autoTriageFreeText === true)
    ) {
      setConditionsMutation.mutate({
        id: definition.id,
        conditions: nextConditions,
        autoTriageFreeText: autoTriage,
      })
    }

    saveMutation.mutate(buildSavePayload(definition))
  }

  // Signature of the current editor content (graphDraft mirrors the live graph
  // + nested rich-text editors). Compared against `savedSignature` for dirty
  // state and, via the ref, against the in-flight save's signature to detect
  // edits made during a save (so the post-save rehydrate never clobbers them).
  const currentSignature = useMemo(
    () =>
      makeContentSignature({
        title,
        description,
        // Mirror the graph + save sources EXACTLY (`definition.steps` /
        // `definition.dependencies`), NOT the `steps`/`dependencies` STATE
        // (which is re-extracted from the TipTap documentBody and can DROP the
        // dependency chain). Using the state here would make an output-only edit
        // round-trip empty deps and fan every node out to Start/Output on save.
        steps: graphDraft?.steps ?? definition?.steps,
        dependencies: graphDraft?.dependencies ?? definition?.dependencies,
        // Only COMPLETE outputs count as a saveable change — an in-progress row
        // (no name or ref yet, e.g. just after "Add output") stays local UI
        // state and must NOT trigger autosave.
        outputs: (graphDraft?.outputs ?? definition?.outputs ?? []).filter(
          (output) => output.name.trim() && output.ref.trim(),
        ),
        paramDrafts: runbookParamDrafts,
      }),
    [
      title,
      description,
      graphDraft,
      steps,
      dependencies,
      definition,
      runbookParamDrafts,
    ]
  )
  const currentSignatureRef = useRef(currentSignature)
  currentSignatureRef.current = currentSignature
  // Signature captured when the active save started (set in onMutate).
  const savingSignatureRef = useRef<string | null>(null)

  const isDirty =
    isDefinitionHydrated &&
    !runbookParamBuild.error &&
    savedSignature !== null &&
    currentSignature !== savedSignature

  const titleText = title.trim() || definition?.title || 'Procedure'
  const displayedValidationResult = validationQuery.data
  const validationErrorCount = displayedValidationResult?.errors.length ?? 0
  // Keep the last completed preflight visible while a draft save/refetch is in
  // flight. Clearing it during autosave makes the title and affected steps
  // briefly look healthy even though no newer validation result exists yet.
  const showValidationErrors = validationErrorCount > 0

  // --- Autosave (policy-editor pattern) -----------------------------------
  // Persist pending edits and AWAIT completion. Used by the debounce below, on
  // unmount, and before actions that read the saved definition (Test Run /
  // Lock / Duplicate / Export). Read via a ref so callers always hit the latest
  // closure without dependency churn. Silent — progress shows in the status
  // pill, and onError surfaces failures.
  const flushPendingSaveRef = useRef<(force?: boolean) => Promise<boolean>>(
    async () => true
  )
  flushPendingSaveRef.current = async (force = false) => {
    if (!definition || !isDefinitionHydrated) return false
    if (
      isDirty &&
      (!canWorkspaceWrite || !title.trim() || runbookParamBuild.error)
    ) {
      return false
    }
    if (!canWorkspaceWrite || !isDirty || !title.trim()) {
      return true
    }
    if (
      !force &&
      failedAutosaveSignatureRef.current === currentSignatureRef.current
    ) {
      return false
    }
    try {
      await saveMutation.mutateAsync(buildSavePayload(definition))
      return true
    } catch {
      /* onError surfaced the failure via toast */
      return false
    }
  }

  // Debounce-persist canvas/param edits 1.5s after the last change. Paused
  // while the Edit-details dialog is open (it commits explicitly) and for
  // locked/viewer/unhydrated/invalid states.
  const canAutosave =
    isDefinitionHydrated &&
    canWorkspaceWrite &&
    !locked &&
    !editDetailsOpen &&
    isDirty &&
    !!title.trim() &&
    !runbookParamBuild.error &&
    failedAutosaveSignatureRef.current !== currentSignature

  useEffect(() => {
    if (!canAutosave || saveMutation.isPending) return
    const timer = setTimeout(() => {
      void flushPendingSaveRef.current()
    }, AUTOSAVE_DEBOUNCE_MS)
    return () => clearTimeout(timer)
    // Reset the debounce on every content change (graphDraft/params/title/
    // description) so we persist 1.5s after the last edit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    canAutosave,
    saveMutation.isPending,
    graphDraft,
    runbookParamBuild,
    title,
    description,
  ])

  // Flush on unmount so edits inside the debounce window survive navigation;
  // clear the "Saved" pill timer too.
  useEffect(
    () => () => {
      void flushPendingSaveRef.current()
      if (savedTimerRef.current) clearTimeout(savedTimerRef.current)
    },
    []
  )

  const handlePublish = async () => {
    const validation = await runCurrentValidation()
    if (!validation?.ok) return
    publishMutation.mutate({ id: runbookId })
  }

  const headerActions =
    canWorkspaceWrite && definition ? (
      <div className='flex items-center gap-2 leading-none [&_button]:h-8 [&_button]:text-[13px] [&_svg]:h-4 [&_svg]:w-4'>
        {locked ? (
          <Badge
            variant='outline'
            className='gap-1 border-border text-muted-foreground'
            title='This procedure is locked. Unlock it from the menu to edit.'
          >
            <Lock className='h-3 w-3' />
            Locked
          </Badge>
        ) : (
          // Autosave status pill (replaces the manual Save button). Changes
          // persist 1.5s after the last edit; this just reflects progress.
          <span className='inline-flex items-center gap-1.5 px-1 text-[12px] text-text-placeholder'>
            {saveMutation.isPending || saveStatus === 'saving' ? (
              <>
                <Loader2 className='h-3.5 w-3.5 animate-spin' />
                Saving…
              </>
            ) : saveStatus === 'saved' ? (
              <>
                <Check className='h-3.5 w-3.5 text-status-green' />
                Saved
              </>
            ) : isDirty ? (
              'Unsaved changes'
            ) : null}
          </span>
        )}
        {showValidationErrors && (
          <button
            type='button'
            className='shrink-0'
            onClick={() => setValidationDialogOpen(true)}
            aria-label='Open procedure validation results'
          >
            <Badge status='red' type='dot' className='text-[10px]'>
              {validationErrorCount}{' '}
              {validationErrorCount === 1 ? 'error' : 'errors'}
            </Badge>
          </button>
        )}
        {/* RFC-200: publish gate. Triggers fire the published version only;
            unpublished changes show a Publish button, otherwise a live chip. */}
        {publishStateQuery.data &&
          (publishStateQuery.data.hasUnpublishedChanges ? (
            <Button
              variant='outline'
              size='sm'
              className='gap-2'
              onClick={() => void handlePublish()}
              disabled={
                !isDefinitionHydrated ||
                locked ||
                saveMutation.isPending ||
                publishMutation.isPending ||
                validationRunning
              }
              title={
                publishStateQuery.data.published
                  ? 'Publish your draft changes. Triggers fire the published version'
                  : 'Publish to activate triggers (validates first)'
              }
            >
              {publishMutation.isPending ? (
                <Loader2 className='animate-spin' />
              ) : (
                <UploadCloud />
              )}
              {publishStateQuery.data.published ? 'Publish changes' : 'Publish'}
            </Button>
          ) : (
            <Badge
              variant='outline'
              className='gap-1 border-border text-muted-foreground'
              title='Live: triggers fire this published version'
            >
              <Check className='h-3 w-3 text-status-green' />
              Published v{publishStateQuery.data.publishedVersion}
            </Badge>
          ))}
        {/* Run is fire-and-forget; gate only on the dispatch window. */}
        <Button
          variant='default'
          size='sm'
          className='bg-primary text-primary-foreground hover:bg-primary/90 gap-2'
          onClick={() => void handleRun()}
          disabled={
            !isDefinitionHydrated ||
            saveMutation.isPending ||
            executeMutation.isPending ||
            validationRunning
          }
        >
          <Play />
          Test Run
        </Button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant='outline'
              size='smIcon'
              aria-label='More actions'
            >
              <MoreHorizontal />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align='end' className='w-44'>
            <DropdownMenuItem onClick={() => setLogsOpen(true)}>
              <History className=' h-4 w-4' />
              Logs
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => setEditDetailsOpen(true)}
              disabled={!isDefinitionHydrated || locked}
            >
              <Pencil className=' h-4 w-4' />
              Edit details
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={async () => {
                await flushPendingSaveRef.current(true)
                lockMutation.mutate({ id: runbookId, locked: !locked })
              }}
              disabled={!isDefinitionHydrated || lockMutation.isPending}
            >
              {locked ? (
                <Unlock className=' h-4 w-4' />
              ) : (
                <Lock className=' h-4 w-4' />
              )}
              {locked ? 'Unlock' : 'Lock'}
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={async () => {
                await flushPendingSaveRef.current(true)
                duplicateMutation.mutate({ id: runbookId })
              }}
              disabled={!isDefinitionHydrated || duplicateMutation.isPending}
            >
              <Copy className=' h-4 w-4' />
              Duplicate
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={handleExport}
              disabled={!isDefinitionHydrated}
            >
              <Download className=' h-4 w-4' />
              Export
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => setVersionHistoryOpen(true)}
              disabled={!isDefinitionHydrated}
            >
              <History className=' h-4 w-4' />
              Version history
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => setDeleteDialogOpen(true)}
              disabled={
                !isDefinitionHydrated || !canDelete || deleteMutation.isPending
              }
              title={deleteBlockedReason}
              variant='destructive'
            >
              <Trash2 className=' h-4 w-4' />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    ) : null

  return (
    <ScreenataPageLayout
      title={titleText}
      raw
      customHeader={
        <div className='flex h-full w-full min-w-0 items-center gap-3 overflow-hidden'>
          <Breadcrumb className='min-w-0 flex-1 overflow-hidden'>
            <BreadcrumbList className='flex-nowrap overflow-hidden'>
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link
                    to='/workspaces/$workspaceId/runbooks'
                    params={{ workspaceId }}
                  >
                    Procedures
                  </Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem className='flex min-w-0 items-center gap-2'>
                <BreadcrumbPage
                  className={cn(
                    'block truncate',
                    showValidationErrors && 'text-status-red'
                  )}
                >
                  {titleText}
                </BreadcrumbPage>
                {showValidationErrors && (
                  <button
                    type='button'
                    className='shrink-0'
                    onClick={() => setValidationDialogOpen(true)}
                    aria-label='Open procedure validation results'
                  >
                    <Badge status='red' type='dot' className='text-[10px]'>
                      {validationErrorCount}{' '}
                      {validationErrorCount === 1 ? 'error' : 'errors'}
                    </Badge>
                  </button>
                )}
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
          <div className='ml-auto flex h-full shrink-0 items-center gap-2 pr-3 leading-none [&_a]:inline-flex'>
            {headerActions}
            <AskAiButton variant='header' />
          </div>
        </div>
      }
      mainClassName='overflow-hidden'
    >
      <div className='flex h-[calc(var(--app-vh)_-_50px)] w-full flex-col border-t border-border'>
        {importedControlIds.length > 0 && !importBannerDismissed && (
          <div className='flex shrink-0 items-center gap-3 border-b border-cobalt-border bg-selected px-4 py-2 text-[12px] text-text-secondary'>
            <Sparkles className='h-4 w-4 shrink-0 text-cobalt-strong' />
            <span className='flex-1'>
              Imported from your SOP: {importedControlIds.length} control
              {importedControlIds.length === 1 ? '' : 's'} suggested. Review the
              steps, then activate to bind them.
            </span>
            {canWorkspaceWrite && (
              <Button
                size='sm'
                className='h-7 gap-1.5 bg-primary text-[12px] text-primary-foreground hover:bg-primary/90'
                onClick={() => {
                  setSelectedCreateTestControlIds(importedControlIds)
                  setCreateTestDialogOpen(true)
                }}
              >
                Review &amp; activate
              </Button>
            )}
            <button
              type='button'
              onClick={() => setImportBannerDismissed(true)}
              className='text-text-subtle hover:text-text-secondary'
              aria-label='Dismiss'
            >
              <X className='h-4 w-4' />
            </button>
          </div>
        )}
        {definitionQuery.isError ? (
          <div className='m-8 flex-1 self-start rounded-sm border border-border bg-card px-4 py-8 text-[13px] text-text-subtle'>
            Failed to load this procedure. Check your connection and try again.
          </div>
        ) : !definition || !isDefinitionHydrated ? (
          <div className='flex-1 space-y-4 p-8'>
            <Skeleton className='h-8 w-72' />
            <Skeleton className='h-[520px] w-full rounded-md' />
          </div>
        ) : (
          <>
            <div className='min-h-0 min-w-0 flex-1'>
              <RunbookDefinitionGraph
                className='h-full w-full'
                steps={displayedSteps}
                selectedStepId={selectedGraphStepId}
                onSelectedStepChange={setSelectedGraphStepId}
                dependencies={
                  graphDraft?.dependencies ?? definition.dependencies
                }
                validationIssues={
                  showValidationErrors
                    ? displayedValidationResult?.errors
                    : undefined
                }
                onFixValidationIssues={
                  canWorkspaceWrite && !locked
                    ? (stepId) => void handleFixValidationIssues(stepId)
                    : undefined
                }
                onViewValidationIssues={() => setValidationDialogOpen(true)}
                outputs={graphDraft?.outputs ?? definition.outputs}
                editable={canWorkspaceWrite && !locked}
                onChange={(payload) =>
                  setGraphDraft((prev) => ({
                    ...payload,
                    outputs: prev?.outputs ?? definition.outputs,
                  }))
                }
                onOutputsChange={(nextOutputs) =>
                  // Editing ONLY outputs must carry over the EXACT steps/deps the
                  // graph is rendering (`definition.*` via the graphDraft
                  // fallback) — NOT the `steps`/`dependencies` STATE, which is
                  // re-extracted from the documentBody and can have an empty
                  // dependency chain. Writing the state here would persist empty
                  // deps and fan every node out to Start/Output on the next save.
                  setGraphDraft((prev) => ({
                    steps: prev?.steps ?? definition.steps,
                    dependencies: prev?.dependencies ?? definition.dependencies,
                    outputs: nextOutputs,
                  }))
                }
                workflowId={runbookId}
                paramNames={Object.keys(runbookParamBuild.params)}
                inputCount={runbookParamDrafts.length}
                // RFC-194 §8.6 — the input contract lives in the Start node, not a
                // separate "Parameters" tab (it was redundant with the start node's
                // inputs). Rendered inside the Start node's panel.
                startInputsPanel={
                  canWorkspaceWrite && !locked ? (
                    <div className='space-y-3'>
                      <RunbookParamEditor
                        drafts={runbookParamDrafts}
                        onChange={setRunbookParamDrafts}
                        usage={runbookInputUsage}
                      />
                      {runbookParamBuild.error && (
                        <div className='rounded-sm border border-status-orange/30 bg-status-orange-bg px-3 py-2 text-[12px] text-status-orange-strong'>
                          {runbookParamBuild.error}
                        </div>
                      )}
                    </div>
                  ) : (
                    <RunbookParamsSection
                      drafts={runbookParamDrafts}
                      editable={false}
                    />
                  )
                }
                startRoutingPanel={
                  <div className='space-y-3'>
                    <Textarea
                      value={conditions}
                      onChange={(event) => setConditions(event.target.value)}
                      aria-label='Routing conditions'
                      placeholder='e.g. "An employee needs access to an internal system"'
                      className='min-h-[88px] text-[13px]'
                      disabled={!canWorkspaceWrite || locked}
                    />
                    <p className='text-text-subtle text-[11px]'>
                      Published procedures with a condition are considered.
                      Leave blank to exclude it.
                    </p>
                    <label className='flex items-start gap-2 text-[12px]'>
                      <Checkbox
                        checked={autoTriage}
                        onCheckedChange={(checked) =>
                          setAutoTriage(checked === true)
                        }
                        className='mt-0.5'
                        disabled={!canWorkspaceWrite || locked}
                      />
                      <span className='text-text-secondary font-medium'>
                        Auto-start only when the match is high-confidence and
                        all required inputs are available
                      </span>
                    </label>
                    <p className='text-text-subtle text-[11px]'>
                      High-confidence matches start; otherwise an admin reviews.
                    </p>
                    {canWorkspaceWrite && !locked && (
                      <Button
                        size='sm'
                        onClick={() =>
                          setConditionsMutation.mutate({
                            id: definition.id,
                            conditions: conditions.trim() || null,
                            autoTriageFreeText: autoTriage,
                          })
                        }
                        disabled={
                          setConditionsMutation.isPending ||
                          ((conditions.trim() || null) ===
                            ((definition.conditions ?? '').trim() || null) &&
                            autoTriage ===
                              (definition.autoTriageFreeText === true))
                        }
                      >
                        {setConditionsMutation.isPending ? (
                          <Loader2 className='animate-spin' />
                        ) : null}
                        Save routing
                      </Button>
                    )}
                  </div>
                }
              />
            </div>
            <LogsDialog
              open={logsOpen}
              onOpenChange={setLogsOpen}
              runs={runsQuery.data ?? []}
              isLoading={runsQuery.isLoading}
              activeRunId={selectedRunId}
              onSelect={(runId) => setSelectedRunId(runId)}
              output={
                selectedRunId && selectedRunDetailsQuery.data ? (
                  <OutputPanel run={selectedRunDetailsQuery.data} />
                ) : null
              }
            />
          </>
        )}
      </div>

      <RunbookRunDetail
        runId={selectedRunId}
        onClose={() => setSelectedRunId(null)}
      />

      <Dialog
        open={createTestDialogOpen}
        onOpenChange={setCreateTestDialogOpen}
      >
        <DialogContent className='max-w-lg'>
          <DialogHeader>
            <DialogTitle className='font-heading'>
              Create Procedure Test
            </DialogTitle>
            <DialogDescription>
              Select the controls this procedure should satisfy. The created test
              will stay linked to this procedure.
            </DialogDescription>
          </DialogHeader>
          <div className='space-y-2'>
            <div className='text-[12px] font-medium text-text-body'>
              Controls
            </div>
            <ControlSelector
              controls={allControls}
              selectedIds={selectedCreateTestControlIds}
              onSelectionChange={setSelectedCreateTestControlIds}
              placeholder={
                controlsQuery.isLoading
                  ? 'Loading controls...'
                  : 'Select controls...'
              }
              searchPlaceholder='Search controls...'
              disabled={
                controlsQuery.isLoading || createLinkedTestMutation.isPending
              }
              popoverPortalled={false}
            />
            {controlsQuery.isError ? (
              <p className='text-[12px] text-status-red-emphasis'>
                Failed to load controls. Close and try again.
              </p>
            ) : null}
          </div>
          <DialogFooter>
            <Button
              variant='outline'
              onClick={() => setCreateTestDialogOpen(false)}
              disabled={createLinkedTestMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={createLinkedTest}
              disabled={
                createLinkedTestMutation.isPending ||
                selectedCreateTestControlIds.length === 0
              }
            >
              {createLinkedTestMutation.isPending ? (
                <Loader2 className='animate-spin' />
              ) : null}
              Create test
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={editDetailsOpen}
        onOpenChange={(open) => {
          if (saveMutation.isPending) return
          if (open) {
            setEditDetailsOpen(true)
          } else {
            // Discard unsaved edits — re-seed from the persisted definition.
            // (Conditions live in the Routing side panel, not this dialog —
            // don't reset them here.)
            setTitle(definition?.title || '')
            setDescription(definition?.description || '')
            setEditDetailsOpen(false)
          }
        }}
      >
        <DialogContent className='max-w-lg'>
          <DialogHeader>
            <DialogTitle className='font-heading'>Edit details</DialogTitle>
            <DialogDescription>
              Update the title and description for this procedure.
            </DialogDescription>
          </DialogHeader>
          <div className='space-y-5 px-6 py-4'>
            <section>
              <div className='text-muted-foreground mb-1.5 text-[10px] font-semibold tracking-[0.08em] uppercase'>
                Title
              </div>
              <Input
                value={title}
                onChange={(event) => setTitle(event.target.value)}
                placeholder='Untitled procedure'
                className='h-9 text-[13px]'
              />
            </section>
            <section>
              <div className='text-muted-foreground mb-1.5 text-[10px] font-semibold tracking-[0.08em] uppercase'>
                Description
              </div>
              <Textarea
                value={description}
                onChange={(event) => setDescription(event.target.value)}
                placeholder='Short summary for operators and reviewers'
                className='min-h-[64px] text-[13px]'
              />
            </section>
          </div>
          <DialogFooter>
            <Button
              variant='outline'
              onClick={() => {
                setTitle(definition?.title || '')
                setDescription(definition?.description || '')
                setEditDetailsOpen(false)
              }}
              disabled={saveMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={saveMutation.isPending || !title.trim()}
            >
              {saveMutation.isPending ? (
                <Loader2 className='animate-spin' />
              ) : null}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={versionHistoryOpen} onOpenChange={setVersionHistoryOpen}>
        <DialogContent className='max-w-lg'>
          <DialogHeader>
            <DialogTitle className='font-heading'>Version history</DialogTitle>
            <DialogDescription>
              Each publish creates an immutable version. Restore one into the
              draft to roll back. Review it, then publish to make it live.
            </DialogDescription>
          </DialogHeader>
          <div className='pb-2'>
            {versionsQuery.isLoading ? (
              <div className='px-6 py-6 text-center text-[12px] text-text-placeholder'>
                Loading…
              </div>
            ) : (versionsQuery.data ?? []).length === 0 ? (
              <div className='px-6 py-6 text-center text-[12px] text-text-placeholder'>
                No published versions yet. Publish the draft to create v1.
              </div>
            ) : (
              <ScrollArea
                type='scroll'
                className='max-h-[320px]'
                viewClassName='[&>div]:!block'
              >
                <div className='divide-y divide-border px-6'>
                  {(versionsQuery.data ?? []).map((entry) => (
                    <div
                      key={entry.version}
                      className='flex items-center justify-between gap-3 py-2.5'
                    >
                      <div className='min-w-0'>
                        <div className='flex items-center gap-2 text-[13px] font-medium text-text-secondary'>
                          v{entry.version}
                          {entry.isCurrent && (
                            <span className='inline-flex items-center gap-1 rounded-full border border-[#CDEBDD] bg-[#F0FBF5] px-1.5 py-0.5 text-[10px] font-medium text-[#0E7A4F]'>
                              Current
                            </span>
                          )}
                        </div>
                        <div className='text-[11px] text-text-placeholder'>
                          {new Date(entry.publishedAt).toLocaleString()}
                        </div>
                      </div>
                      {!entry.isCurrent && (
                        <Button
                          variant='outline'
                          size='sm'
                          onClick={() =>
                            restoreVersionMutation.mutate({
                              id: runbookId,
                              version: entry.version,
                            })
                          }
                          disabled={restoreVersionMutation.isPending}
                        >
                          {restoreVersionMutation.isPending ? (
                            <Loader2 className='h-4 w-4 animate-spin' />
                          ) : null}
                          Restore to draft
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              </ScrollArea>
            )}
          </div>
          <DialogFooter>
            <Button
              variant='outline'
              size='sm'
              onClick={() => setVersionHistoryOpen(false)}
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={validationDialogOpen}
        onOpenChange={setValidationDialogOpen}
      >
        <DialogContent className='grid max-h-[calc(100dvh-2rem)] max-w-2xl grid-rows-[auto_minmax(0,1fr)_auto] overflow-hidden'>
          <DialogHeader>
            <DialogTitle className='font-heading flex items-center gap-2'>
              {displayedValidationResult?.ok ? (
                <Check className='text-status-green h-4 w-4' />
              ) : (
                <AlertTriangle className='text-destructive h-4 w-4' />
              )}
              {displayedValidationResult?.ok
                ? 'Validation passed'
                : 'Validation failed'}
            </DialogTitle>
            <DialogDescription>
              {displayedValidationResult
                ? `${displayedValidationResult.errors.length} errors · ${displayedValidationResult.warnings.length} warnings · ${displayedValidationResult.unverified.length} unverified`
                : 'No validation result is available.'}
            </DialogDescription>
          </DialogHeader>
          <div className='min-h-0'>
            <ScrollArea
              type='always'
              className='h-full'
              viewClassName='[&>div]:!block'
            >
              <div className='space-y-5 px-6'>
                {displayedValidationResult?.ok &&
                displayedValidationResult.warnings.length === 0 &&
                displayedValidationResult.unverified.length === 0 ? (
                  <div className='border-status-green/30 bg-status-green-bg text-text-body rounded-md border p-4 text-[13px]'>
                    No definition, tool wiring, provider capability, or
                    connected Integration issues were found.
                  </div>
                ) : null}

                {[
                  {
                    label: 'Errors',
                    issues: displayedValidationResult?.errors ?? [],
                    labelClassName: 'text-destructive',
                    issueClassName: 'border-destructive/25 bg-destructive/5',
                  },
                  {
                    label: 'Warnings',
                    issues: displayedValidationResult?.warnings ?? [],
                    labelClassName: 'text-status-orange',
                    issueClassName:
                      'border-status-orange/30 bg-status-orange-bg',
                  },
                  {
                    label: 'Unverified',
                    issues: displayedValidationResult?.unverified ?? [],
                    labelClassName: 'text-muted-foreground',
                    issueClassName: 'border bg-muted/30',
                  },
                ].map((section) =>
                  section.issues.length > 0 ? (
                    <section key={section.label} className='space-y-2'>
                      <div
                        className={cn(
                          'text-[10px] font-semibold tracking-[0.08em] uppercase',
                          section.labelClassName
                        )}
                      >
                        {section.label}
                      </div>
                      {section.issues.map((issue, index) => {
                        const location = getRunbookPreflightIssueLocation(
                          issue,
                          displayedSteps
                        )
                        return (
                          <div
                            key={`${issue.code}-${issue.stepId ?? 'runbook'}-${issue.actionIndex ?? index}-${issue.param ?? ''}`}
                            className={cn(
                              'rounded-md border p-3',
                              section.issueClassName
                            )}
                          >
                            <div
                              className={cn(
                                'text-[11px] font-medium',
                                section.labelClassName
                              )}
                            >
                              {location.join(' · ') || issue.code}
                            </div>
                            <p className='text-text-body mt-1 text-[13px]'>
                              {issue.message}
                            </p>
                            {issue.suggestion ? (
                              <p className='text-muted-foreground mt-1.5 text-[12px]'>
                                {issue.suggestion}
                              </p>
                            ) : null}
                          </div>
                        )
                      })}
                    </section>
                  ) : null
                )}

                <p className='border-border text-muted-foreground border-t pt-3 text-[11px] leading-5'>
                  Validate checks the saved draft and current Integration records. It does not call external APIs or execute tools, so runtime data and provider availability can still affect a Test Run.
                </p>
              </div>
            </ScrollArea>
          </div>
          <DialogFooter>
            <Button
              variant='outline'
              onClick={() => setValidationDialogOpen(false)}
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={runInputDialogOpen} onOpenChange={setRunInputDialogOpen}>
        <DialogContent className='grid max-h-[calc(100dvh-2rem)] max-w-lg grid-rows-[auto_minmax(0,1fr)_auto] overflow-hidden'>
          <DialogHeader>
            <DialogTitle className='font-heading'>Run Procedure</DialogTitle>
            <DialogDescription>
              Provide any execution parameters referenced by this procedure before
              execution.
            </DialogDescription>
          </DialogHeader>
          <div className='min-h-0 overflow-y-auto px-6 py-4'>
            <RunbookInputsForm
              params={effectiveRunParams}
              values={runbookInputValues}
              onChange={setRunbookInputValues}
              disabled={executeMutation.isPending}
            />
          </div>
          <DialogFooter>
            <Button
              variant='outline'
              onClick={() => setRunInputDialogOpen(false)}
              disabled={executeMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                setRunInputDialogOpen(false)
                executeRunbook()
              }}
              disabled={executeMutation.isPending}
            >
              {executeMutation.isPending ? (
                <Loader2 className='h-4 w-4 animate-spin' />
              ) : (
                <Play className='h-4 w-4' />
              )}
              Run
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={deleteDialogOpen}
        onOpenChange={(open) => {
          if (!deleteMutation.isPending) setDeleteDialogOpen(open)
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className='font-heading'>
              Delete this procedure?
            </AlertDialogTitle>
            <AlertDialogDescription>
              {`"${title.trim() || definition?.title || 'Untitled procedure'}" will be removed permanently along with all its run history. This can't be undone.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteMutation.isPending}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              className='bg-destructive-emphasis text-white hover:bg-[#A01818]'
              disabled={deleteMutation.isPending}
              onClick={(event) => {
                // Stop the default close-on-click so we can wait for
                // the mutation; AlertDialog otherwise dismisses the
                // dialog before the request completes.
                event.preventDefault()
                deleteMutation.mutate({ id: runbookId })
              }}
            >
              {deleteMutation.isPending ? (
                <>
                  <Loader2 className=' h-4 w-4 animate-spin' />
                  Deleting…
                </>
              ) : (
                'Delete procedure'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ScreenataPageLayout>
  )
}
